// Assistent fuer eine neue Serienmail: drei Schritte (Empfaenger, Inhalt, Pruefen),
// gehalten in EINEM Entwurfszustand nach dem Muster von UebergabeAssistent.tsx
// (pages/admin/uebergabe) — die Schritt-Komponenten bekommen `zustand` + `setZustand`
// und aendern nie etwas an geladenen Referenzdaten (Objekte, Vorlagen, ...) selbst.
//
// Optik: neues Design des E-Mail-Bereichs (Handoff E-Mails §8, Schrittleiste + Glas-Karten).
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretLeft, PaperPlaneTilt, PencilSimple, Users, WarningCircle } from '@phosphor-icons/react';
import { ImmoButton, Schrittleiste } from '../../../components/immobilien/ui';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { SERIENMAIL_PFAD } from './SerienmailListe';
import { supabase } from '../../../lib/supabase/client';
import { ladeEinheiten, ladeKandidaten, ladeObjekte } from '../../../lib/serienmail/api';
import { teileAuf } from '../../../lib/serienmail/aufteilung';
import type { Kandidat, Zielgruppe } from '../../../lib/serienmail/typen';
import SchrittEmpfaenger from './SchrittEmpfaenger';
import SchrittInhalt from './SchrittInhalt';
import SchrittPruefen from './SchrittPruefen';
import Druckansicht from './Druckansicht';

export interface Zustand {
  // Schritt 1 — Empfaenger
  /** #577: mehrere Gruppen gleichzeitig (Mieter, Eigentümer, Dienstleister, sonstige). */
  zielgruppen: Zielgruppe[];
  /** #577: 'objekte' = nur gewählte Objekte, 'alle' = global ohne Objektauswahl. */
  bereich: 'objekte' | 'alle';
  objektIds: string[];
  einheitIds: string[];
  /** owner_id der Erreichbaren, die per Haekchen abgewaehlt wurden. */
  abgewaehlt: Set<string>;

  // Schritt 2 — Inhalt
  betreff: string;
  /** Nur der eigentliche Inhalt (ohne Briefpapier/Signatur), MIT Platzhaltern. */
  html: string;
  vorlageId: string | null;
  briefpapierId: string;
  signaturId: string;
  anhaenge: { name: string; path: string; size: number }[];

  // Schritt 3 — Pruefen und senden
  vorschauIndex: number;
  geplantModus: 'sofort' | 'geplant';
  geplantDatum: string;
  geplantZeit: string;
  bestaetigungEingabe: string;
}

export type SetZustand = (updater: Zustand | ((v: Zustand) => Zustand)) => void;

const ANFANGSZUSTAND: Zustand = {
  zielgruppen: ['mieter'],
  bereich: 'objekte',
  objektIds: [],
  einheitIds: [],
  abgewaehlt: new Set(),
  betreff: '',
  html: '',
  vorlageId: null,
  briefpapierId: '',
  signaturId: '',
  anhaenge: [],
  vorschauIndex: 0,
  geplantModus: 'sofort',
  geplantDatum: '',
  geplantZeit: '',
  bestaetigungEingabe: '',
};

export interface Objekt { id: string; bezeichnung: string; }
export interface Einheit { id: string; objekt_id: string; bezeichnung: string; }
export interface Vorlage { id: string; name: string; betreff_template: string | null; html_template: string | null; }
export interface Briefpapier { id: string; name: string; html: string; ist_standard: boolean; }
export interface Signatur { id: string; name: string; html_signatur: string; ist_standard: boolean; }
export interface Konto { id: string; email_adresse: string; absender_name: string | null; bezeichnung: string; }

type Schritt = 1 | 2 | 3;
const SCHRITT_LABEL: Record<Schritt, string> = { 1: 'Empfänger', 2: 'Inhalt', 3: 'Prüfen und senden' };
const SCHRITTE = [
  { label: SCHRITT_LABEL[1], icon: Users },
  { label: SCHRITT_LABEL[2], icon: PencilSimple },
  { label: SCHRITT_LABEL[3], icon: PaperPlaneTilt },
] as const;

const SerienmailAssistent: React.FC = () => {
  const navigate = useNavigate();
  const { mobil } = useMobilModus();
  const [schritt, setSchritt] = useState<Schritt>(1);
  const [zustand, setZustandState] = useState<Zustand>(ANFANGSZUSTAND);
  const setZustand: SetZustand = (updater) =>
    setZustandState((vorher) => (typeof updater === 'function' ? (updater as (v: Zustand) => Zustand)(vorher) : updater));

  // ── Referenzdaten: Objekte/Einheiten (Empfaenger) ──────────────────────────
  const [objekte, setObjekte] = useState<Objekt[]>([]);
  const [einheiten, setEinheiten] = useState<Einheit[]>([]);
  const [objekteFehler, setObjekteFehler] = useState<string | null>(null);

  useEffect(() => {
    ladeObjekte().then(setObjekte).catch((err) => setObjekteFehler(err.message));
  }, []);

  useEffect(() => {
    setEinheiten([]);
    if (zustand.objektIds.length === 0) return;
    ladeEinheiten(zustand.objektIds).then(setEinheiten).catch(() => setEinheiten([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zustand.objektIds.join(',')]);

  // ── Kandidaten neu laden bei jeder Aenderung der Empfaenger-Auswahl ────────
  const [kandidaten, setKandidaten] = useState<Kandidat[]>([]);
  // #579: Zahl der wegen „nicht aktiv“ ausgeblendeten Kontakte (nur zur Anzeige)
  const [ausgeblendet, setAusgeblendet] = useState(0);
  const [kandidatenLoading, setKandidatenLoading] = useState(false);
  const [kandidatenFehler, setKandidatenFehler] = useState<string | null>(null);

  useEffect(() => {
    let abgebrochen = false;
    setKandidatenFehler(null);
    const alle = zustand.bereich === 'alle';
    if (zustand.zielgruppen.length === 0 || (!alle && zustand.objektIds.length === 0)) { setKandidaten([]); setAusgeblendet(0); return; }
    setKandidatenLoading(true);
    ladeKandidaten(zustand.zielgruppen, alle ? { alle: true, objektIds: [], einheitIds: [] } : { objektIds: zustand.objektIds, einheitIds: zustand.einheitIds })
      .then(({ kandidaten: liste, ausgeblendet: anzahl }) => {
        if (abgebrochen) return;
        setKandidaten(liste);
        setAusgeblendet(anzahl);
        // Neue Auswahl: alle Erreichbaren wieder vorausgewaehlt — sonst bliebe eine
        // Abwahl vom vorigen Objekt an einer voellig anderen Trefferliste haengen.
        setZustand((z) => ({ ...z, abgewaehlt: new Set() }));
      })
      .catch((err) => { if (!abgebrochen) setKandidatenFehler(err.message); })
      .finally(() => { if (!abgebrochen) setKandidatenLoading(false); });
    return () => { abgebrochen = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zustand.zielgruppen.join(','), zustand.bereich, zustand.objektIds.join(','), zustand.einheitIds.join(',')]);

  // Aufteilung IMMER ueber teileAuf — Zaehler, Trefferliste, Druckliste und CSV
  // duerfen nie eigene Rechnungen aufmachen, sonst laufen die Zahlen auseinander.
  // Abschluss-Review I10: `doppelt` mitfuehren — Personen, die sich eine Adresse teilen
  // (Ehepaare), stehen in keiner der beiden Listen und wuerden sonst spurlos fehlen.
  const { erreichbar, ohneAdresse, doppelt } = useMemo(() => teileAuf(kandidaten), [kandidaten]);
  const ausgewaehlt = useMemo(
    () => erreichbar.filter((k) => !zustand.abgewaehlt.has(k.owner_id)),
    [erreichbar, zustand.abgewaehlt],
  );

  // ── Referenzdaten: Vorlagen, Briefpapier, Signaturen, Absender-Konto ───────
  const [vorlagen, setVorlagen] = useState<Vorlage[]>([]);
  const [briefpapierListe, setBriefpapierListe] = useState<Briefpapier[]>([]);
  const [signaturenListe, setSignaturenListe] = useState<Signatur[]>([]);
  const [konto, setKonto] = useState<Konto | null>(null);
  // Ladefehler dieser vier Abfragen wurden bislang verschluckt (kein error-Check, kein
  // catch) — ein RLS- oder Netzfehler blieb unsichtbar. Bei Vorlage/Briefpapier/
  // Signatur reicht ein Hinweis (der Nutzer merkt eine leere Auswahl ohnehin). Beim
  // KONTO ist das anders: absender.name/absender.email werden bereits beim Anlegen des
  // Laufs in JEDE der bis zu hunderte Empfaenger-Zeilen eingefroren (legeSerienmailAn).
  // Bleibt `konto` durch einen verschluckten Fehler null, ginge ein Rundschreiben mit
  // leerem Absender raus, ohne dass je eine Meldung auf dem Bildschirm stand — deshalb
  // blockiert `kontoFehler` unten zusaetzlich den Startknopf in SchrittPruefen.
  const [vorlagenFehler, setVorlagenFehler] = useState<string | null>(null);
  const [briefpapierFehler, setBriefpapierFehler] = useState<string | null>(null);
  const [signaturenFehler, setSignaturenFehler] = useState<string | null>(null);
  const [kontoFehler, setKontoFehler] = useState<string | null>(null);

  useEffect(() => {
    supabase.from('email_vorlagen').select('id, name, betreff_template, html_template').order('name')
      .then(({ data, error }) => {
        if (error) { setVorlagenFehler(error.message); return; }
        if (data) setVorlagen(data as Vorlage[]);
      });

    supabase.from('email_briefpapier').select('id, name, html, ist_standard')
      .order('ist_standard', { ascending: false }).order('name')
      .then(({ data, error }) => {
        if (error) { setBriefpapierFehler(error.message); return; }
        if (!data) return;
        setBriefpapierListe(data as Briefpapier[]);
        const std = (data as Briefpapier[]).find((b) => b.ist_standard) || (data.length === 1 ? data[0] : null);
        if (std) setZustand((z) => ({ ...z, briefpapierId: std.id }));
      });

    supabase.from('email_signaturen').select('id, name, html_signatur, ist_standard')
      .order('ist_standard', { ascending: false }).order('name')
      .then(({ data, error }) => {
        if (error) { setSignaturenFehler(error.message); return; }
        if (!data) return;
        setSignaturenListe(data as Signatur[]);
        // Genauso wie beim Briefpapier NICHT einfach die erste geladene Signatur nehmen:
        // die DB-Reihenfolge ist zufaellig, und eine falsch vorausgewaehlte Signatur unter
        // einem Rundschreiben (z. B. eine private/Test-Signatur) ist schlimmer als gar keine.
        // Nur vorauswaehlen, wenn es eindeutig ist — genau eine Signatur oder eine als
        // Standard markierte; sonst bleibt „Keine Signatur" stehen und der Nutzer entscheidet.
        const std = (data as Signatur[]).find((s) => s.ist_standard) || (data.length === 1 ? data[0] : null);
        if (std) setZustand((z) => ({ ...z, signaturId: std.id }));
      });

    // Genau EIN Postfach im Projekt (info@, siehe Spec) — das aktive bzw. das
    // Standard-Konto ist der Absender fuer alle Serienmail-Laeufe.
    supabase.from('email_konten').select('id, email_adresse, absender_name, bezeichnung').eq('aktiv', true)
      .order('ist_standard', { ascending: false }).limit(1).maybeSingle()
      .then(({ data, error }) => {
        if (error) { setKontoFehler(error.message); return; }
        if (data) setKonto(data as Konto); else setKontoFehler('Kein aktives E-Mail-Konto gefunden.');
      });
  }, []);

  const [druckansichtOffen, setDruckansichtOffen] = useState(false);

  // ── Navigation ──────────────────────────────────────────────────────────
  const weiterErlaubt = schritt === 1 ? ausgewaehlt.length > 0 || ohneAdresse.length > 0 : true;

  const kh = mobil ? 44 : 40;

  return (
    <div className="flex flex-col min-w-0" style={{ gap: 14 }}>
      <div className="flex items-center flex-wrap" style={{ gap: 12 }}>
        <ImmoButton groesse={kh} icon={CaretLeft} onClick={() => navigate(SERIENMAIL_PFAD)}>Zurück zur Übersicht</ImmoButton>
        <div className="flex flex-col min-w-0" style={{ gap: 1 }}>
          <h2 className="m-0" style={{ fontSize: 18, fontWeight: 800 }}>Neue Serienmail</h2>
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Schritt {schritt} von 3 — {SCHRITT_LABEL[schritt]}</span>
        </div>
      </div>

      {/* Schrittanzeige: erledigte Schritte zurück anklickbar, der nächste nur, wenn „Weiter“ erlaubt ist */}
      <Schrittleiste
        schritte={SCHRITTE}
        aktuell={schritt - 1}
        mobil={mobil}
        darfWaehlen={(i) => { const s = (i + 1) as Schritt; return s < schritt || (s === schritt + 1 && weiterErlaubt); }}
        onWahl={(i) => setSchritt((i + 1) as Schritt)}
      />

      {[objekteFehler, vorlagenFehler, briefpapierFehler, signaturenFehler, kontoFehler].filter(Boolean).map((fehler, i) => (
        <div key={i} role="alert" className="flex items-center" style={{ gap: 8, borderRadius: 14, padding: '10px 14px', background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13, fontWeight: 700 }}>
          <WarningCircle size={16} weight="bold" className="shrink-0" />
          <span>{fehler}</span>
        </div>
      ))}

      {schritt === 1 && (
        <SchrittEmpfaenger
          zustand={zustand}
          setZustand={setZustand}
          objekte={objekte}
          einheiten={einheiten}
          kandidatenLoading={kandidatenLoading}
          kandidatenFehler={kandidatenFehler}
          ausgeblendet={ausgeblendet}
          erreichbar={erreichbar}
          ohneAdresse={ohneAdresse}
          doppelt={doppelt}
          onDruckansichtOeffnen={() => setDruckansichtOffen(true)}
        />
      )}

      {schritt === 2 && (
        <SchrittInhalt
          zustand={zustand}
          setZustand={setZustand}
          vorlagen={vorlagen}
          briefpapierListe={briefpapierListe}
          signaturenListe={signaturenListe}
        />
      )}

      {schritt === 3 && (
        <SchrittPruefen
          zustand={zustand}
          setZustand={setZustand}
          ausgewaehlt={ausgewaehlt}
          briefpapierListe={briefpapierListe}
          signaturenListe={signaturenListe}
          konto={konto}
          kontoFehler={kontoFehler}
          briefAlle={kandidaten.filter((k) => !zustand.abgewaehlt.has(k.owner_id))}
          briefOhneEmail={ohneAdresse}
        />
      )}

      {/* Weiter/Zurueck — Schritt 3 hat einen eigenen Startknopf, hier nur „Zurueck" */}
      <div className="flex items-center justify-between" style={{ gap: 8 }}>
        {schritt > 1 ? (
          <ImmoButton groesse={44} icon={CaretLeft} onClick={() => setSchritt((s) => (s > 1 ? ((s - 1) as Schritt) : s))}>Zurück</ImmoButton>
        ) : <span />}
        {schritt < 3 && (
          <ImmoButton art="akzent" groesse={44} onClick={() => setSchritt((s) => ((s + 1) as Schritt))} disabled={!weiterErlaubt} style={{ opacity: weiterErlaubt ? 1 : 0.45 }}>
            Weiter
          </ImmoButton>
        )}
      </div>

      {druckansichtOffen && (
        <Druckansicht betreff={zustand.betreff} ohneAdresse={ohneAdresse} onClose={() => setDruckansichtOffen(false)} />
      )}
    </div>
  );
};

export default SerienmailAssistent;
